import { createFileRoute } from "@tanstack/react-router";
import heroBg from "@/assets/heroes/colorado-web-design-hero.jpg";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";
import { ColoradoLocalPresence } from "@/components/ColoradoLocalPresence";
import { Link } from "@tanstack/react-router";
import { SERVICE_AREAS } from "@/lib/serviceAreaWebDesign";
import { BUSINESS_ADDRESS_FULL, enhanceColoradoLocalHead } from "@/lib/coloradoLocalSeo";

const CONFIG: SpokePageConfig = {
  heroBackground: { src: heroBg, alt: "Colorado web design and website development desk with Rocky Mountain view — Colorado Web Impressions" },
  slug: "colorado-web-design",
  metaTitle: "Colorado Web Design Company | Websites from $1,850",
  metaDescription: "Colorado web design from our Woodmen Rd office in Colorado Springs. Websites from $1,850 for Denver, Pueblo, Castle Rock and beyond. 27 years of experience.",
  breadcrumbs: [{ label: "Home", to: "/" }],
  currentLabel: "Colorado Web Design",
  eyebrow: "Web Design Company — Colorado",
  h1: (
    <>
      <span className="text-brand-gold">Colorado Web Design</span> Built to Bring in Calls
    </>
  ),
  heroDescription: "Flat-rate websites for Colorado businesses, from Colorado Springs to Fort Collins.",
  intro: "Looking for a Colorado web design company? Chris Heidlebaugh has 27 years of experience building and rebuilding websites for all kinds of businesses. Every site is fast, mobile-friendly and set up so Google, Google Maps and AI tools can find you. Prices are published, and typical sites are ready in under 7 business days.",
  definition: { term: "Colorado web design", body: "A website built for Colorado customers, with town and service pages that match how locals search." },
  whatWeDo: [
    { title: "Service pages", body: "Explain each service clearly, with answers to the questions Colorado customers ask before calling." },
    { title: "Local coverage", body: "Useful city pages for the places you actually serve, without implying offices you do not have." },
    { title: "Local identity", body: "Your real business name, address and phone connected to your Google profile and existing listings." },
    { title: "Fast delivery", body: "Typical sites ready in under 7 business days." },
    { title: "Extra pages", body: "$85 per extra page when you grow." },
    { title: "Free mockup", body: "See your new homepage before you decide." },
  ],
  whyThisMarket: [
    { heading: "Locals search by town", body: "Pages for Colorado Springs, Denver, Castle Rock and more catch nearby buyers." },
    { heading: "Built in Colorado", body: "Based on Woodmen Rd in Colorado Springs, serving the whole state." },
    { heading: "No long contracts", body: "Pay once for the site. Monthly marketing is month-to-month." },
  ],
  relatedLinks: [
    { to: "/learn/web-design-in-colorado", label: "Guide: Web design in Colorado" },
    { to: "/pricing", label: "Pricing" },
    { to: "/colorado-seo", label: "Colorado SEO services" },
    { to: "/colorado-springs-web-design", label: "Colorado Springs web design" },
    { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
    { to: "/colorado-springs-headquarters", label: "Colorado Springs headquarters" },
    { to: "/colorado-springs-office-woodmen-rd", label: "Woodmen Rd office and business listings" },
    { to: "/pikes-peak-contractor-marketing", label: "Pikes Peak contractor marketing" },
    { to: "/south-metro-contractor-marketing", label: "South Metro contractor marketing" },
    { to: "/colorado-marketing-seo-web-design-costs", label: "Colorado web design costs" },
    { to: "/free-mockup", label: "Get a free website mockup" },
  ],
  faqs: [
    { q: "How much does a website cost in Colorado?", a: "Our websites are $1,850 (Starter), $2,750 (Pro) or $5,950 (Expert), one-time, with one year of hosting and SSL included." },
    { q: "How fast can my site be ready?", a: "Typical sites are ready in under 7 business days." },
    { q: "Do you work outside Colorado Springs?", a: "Yes. We build for businesses across Colorado, including Denver, Castle Rock, Parker, Fort Collins, Boulder and Pueblo." },
    { q: "Do I have to sign a contract?", a: "No long contract. The website is a one-time price, and monthly marketing is month-to-month." },
    { q: "Where is your Colorado web design company based?", a: `Colorado Web Impressions is based at ${BUSINESS_ADDRESS_FULL}. We serve businesses statewide from our Colorado Springs home base.` },
    { q: "Does a city page mean you have an office there?", a: "No. City pages describe the markets we serve. Our office is in Colorado Springs; serving Denver or Pueblo does not mean we have a separate location there." },
  ],
  serviceName: "Colorado Web Design",
  serviceDescription: "Flat-rate website design for Colorado businesses from $1,850 with one year of hosting.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/colorado-web-design")({
  head: () => { const head = buildSpokeHead(CONFIG); return { ...head, scripts: enhanceColoradoLocalHead(head, "web") }; },
  component: () => <SpokePage config={CONFIG} extraSections={<><AreaLinks /><ColoradoLocalPresence service="web" /></>} />,
});

function AreaLinks() {
  return (
    <section className="bg-background py-12">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <h2 className="text-2xl font-bold text-foreground">Web design across Colorado</h2>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {Object.values(SERVICE_AREAS).map((a) => (
            <Link key={a.path} to={a.path} className="rounded-lg border border-border px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted">
              Web design in {a.name.replace(/^the /, "")} →
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
